/**
 * Creazione manuale di un Ticket (FKA "Triage Vulnerabilità") su una o più release.
 *
 * L'analista inserisce un titolo e una descrizione libera, poi compone l'elenco delle release
 * coinvolte con il ReleasePicker: cerca un prodotto (filtro locale sul catalogo già in cache),
 * trova spuntate tutte le sue release (caricate solo per il prodotto scelto) e deseleziona quelle
 * non coinvolte, con i pulsanti Seleziona/Deseleziona tutte per il gruppo; ripete per altri
 * prodotti. Le release scelte restano visibili come chip rimovibili. Il Ticket nasce senza CVE:
 * si assegnano dopo dal dettaglio (CvePicker).
 */

import { ModalPortal } from '../../components/ModalPortal.js';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api/client.js';
import { useProducts, useReleases } from '../products/hooks.js';
import type { Product } from '../products/types.js';
import { useCreateTicket } from './hooks.js';
import type { Ticket, TicketRelease } from './types.js';
import {
  SorgenteFields,
  sorgenteIniziale,
  sorgenteInInput,
  sorgenteValida,
  type SorgenteForm,
} from './TicketSourceFields.js';
import styles from './Triage.module.css';

/** Numero massimo di prodotti suggeriti dal filtro. */
const MAX_SUGGERIMENTI = 8;

/** Release selezionata nel picker (senza UUID DT: lo risolve il server). */
export type ReleaseSelezionata = Pick<
  TicketRelease,
  'productInternalId' | 'productName' | 'releaseVersion'
>;

const chiave = (r: Pick<TicketRelease, 'productInternalId' | 'releaseVersion'>) =>
  `${r.productInternalId}@${r.releaseVersion}`;

function messaggioErrore(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Errore sconosciuto';
}

// ---------------------------------------------------------------------------
// Release del prodotto scelto (checkbox)
// ---------------------------------------------------------------------------

function ReleaseDelProdotto({
  prodotto,
  selezionate,
  onToggle,
  onSelezionaTutte,
  onDeselezionaTutte,
}: {
  prodotto: Product;
  selezionate: Set<string>;
  onToggle: (r: ReleaseSelezionata) => void;
  onSelezionaTutte: (rs: ReleaseSelezionata[]) => void;
  onDeselezionaTutte: (rs: ReleaseSelezionata[]) => void;
}) {
  const { data: releases, isLoading, isError, error } = useReleases(prodotto.internalId);
  const preselezionato = useRef(false);

  const voci: ReleaseSelezionata[] = (releases ?? []).map((r) => ({
    productInternalId: prodotto.internalId,
    productName: prodotto.name,
    releaseVersion: r.version,
  }));

  // Scegliere un prodotto spunta di default tutte le sue release (una sola volta per scelta:
  // il picker rimonta questo componente a ogni scelta, quindi le deselezioni successive restano).
  useEffect(() => {
    if (preselezionato.current || isLoading || isError || voci.length === 0) return;
    preselezionato.current = true;
    onSelezionaTutte(voci);
  }, [isLoading, isError, voci.length]);

  if (isLoading) {
    return <p className={styles.loadingMsg}>Caricamento release…</p>;
  }
  if (isError) {
    return <div className={styles.errorMsg}>{messaggioErrore(error)}</div>;
  }
  if (voci.length === 0) {
    return (
      <p className={styles.pickerHint} data-testid="picker-nessuna-release">
        {prodotto.name} non ha release registrate.
      </p>
    );
  }
  const scelte = voci.filter((v) => selezionate.has(chiave(v))).length;
  const tutteScelte = scelte === voci.length;
  const nessunaScelta = scelte === 0;

  return (
    <fieldset className={styles.pickerReleases} data-testid="picker-release-prodotto">
      <legend className={styles.formGroupLabel}>
        Release di <strong>{prodotto.name}</strong>{' '}
        <span className={styles.comboMeta} data-testid="picker-conteggio">
          {scelte} di {voci.length}
        </span>
      </legend>
      <div className={styles.pickerBulk} role="group" aria-label="Selezione rapida">
        <button
          type="button"
          className={styles.pickerBulkButton}
          onClick={() => onSelezionaTutte(voci)}
          disabled={tutteScelte}
          data-testid="picker-seleziona-tutte"
        >
          Seleziona tutte
        </button>
        <button
          type="button"
          className={styles.pickerBulkButton}
          onClick={() => onDeselezionaTutte(voci)}
          disabled={nessunaScelta}
          data-testid="picker-deseleziona-tutte"
        >
          Deseleziona tutte
        </button>
      </div>
      <div className={styles.pickerReleaseGrid}>
        {voci.map((v) => (
          <label key={v.releaseVersion} className={styles.pickerCheck}>
            <input
              type="checkbox"
              checked={selezionate.has(chiave(v))}
              onChange={() => onToggle(v)}
              data-testid={`picker-release-${v.productInternalId}-${v.releaseVersion}`}
            />
            <span style={{ fontFamily: 'monospace' }}>{v.releaseVersion}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// ReleasePicker
// ---------------------------------------------------------------------------

/**
 * Selettore multi-release: ricerca prodotto (combobox con navigazione da tastiera) →
 * checkbox delle sue release → chip delle release scelte (rimovibili).
 */
export function ReleasePicker({
  value,
  onChange,
}: {
  value: ReleaseSelezionata[];
  onChange: (next: ReleaseSelezionata[]) => void;
}) {
  const listId = useId();
  const { data: prodotti, isLoading } = useProducts();
  const [filtro, setFiltro] = useState('');
  const [aperto, setAperto] = useState(false);
  const [attivo, setAttivo] = useState(0);
  const [prodotto, setProdotto] = useState<Product | null>(null);
  // Cambia a ogni scelta: rimonta l'elenco release così la preselezione riparte.
  const [scelta, setScelta] = useState(0);

  const selezionate = useMemo(() => new Set(value.map(chiave)), [value]);

  const suggerimenti = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    const lista = prodotti ?? [];
    const match = f
      ? lista.filter(
          (p) => p.name.toLowerCase().includes(f) || p.internalId.toLowerCase().includes(f),
        )
      : lista;
    return match.slice(0, MAX_SUGGERIMENTI);
  }, [prodotti, filtro]);

  function scegli(p: Product) {
    setProdotto(p);
    setScelta((n) => n + 1);
    setFiltro(p.name);
    setAperto(false);
  }

  function toggle(r: ReleaseSelezionata) {
    onChange(
      selezionate.has(chiave(r)) ? value.filter((v) => chiave(v) !== chiave(r)) : [...value, r],
    );
  }

  function selezionaTutte(rs: ReleaseSelezionata[]) {
    onChange([...value, ...rs.filter((r) => !selezionate.has(chiave(r)))]);
  }

  function deselezionaTutte(rs: ReleaseSelezionata[]) {
    const da = new Set(rs.map(chiave));
    onChange(value.filter((v) => !da.has(chiave(v))));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAperto(true);
      setAttivo((i) => Math.min(i + 1, suggerimenti.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAttivo((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && aperto && suggerimenti[attivo]) {
      e.preventDefault();
      scegli(suggerimenti[attivo]);
    } else if (e.key === 'Escape') {
      setAperto(false);
    }
  }

  return (
    <div className={styles.picker} data-testid="release-picker">
      {/* Release scelte */}
      <div className={styles.bomChips} aria-live="polite" data-testid="picker-selezionate">
        {value.length === 0 ? (
          <span className={styles.pickerHint}>Nessuna release selezionata.</span>
        ) : (
          value.map((r) => (
            <span key={chiave(r)} className={styles.chipRemovable}>
              {r.productName} · {r.releaseVersion}
              <button
                type="button"
                aria-label={`Rimuovi ${r.productName} ${r.releaseVersion}`}
                onClick={() => toggle(r)}
                data-testid={`picker-rimuovi-${r.productInternalId}-${r.releaseVersion}`}
              >
                ✕
              </button>
            </span>
          ))
        )}
      </div>

      {/* Ricerca prodotto */}
      <div className={styles.comboWrap}>
        <input
          type="text"
          role="combobox"
          aria-expanded={aperto}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={aperto && suggerimenti[attivo] ? `${listId}-${attivo}` : undefined}
          className={styles.formSelect}
          style={{ width: '100%' }}
          placeholder={isLoading ? 'Caricamento prodotti…' : 'Cerca prodotto per nome o ID…'}
          value={filtro}
          onChange={(e) => {
            setFiltro(e.target.value);
            setAperto(true);
            setAttivo(0);
          }}
          onFocus={() => setAperto(true)}
          onBlur={() => setTimeout(() => setAperto(false), 150)}
          onKeyDown={onKeyDown}
          data-testid="picker-cerca-prodotto"
        />
        {aperto && suggerimenti.length > 0 && (
          <ul
            id={listId}
            role="listbox"
            className={`${styles.comboList} ${styles.comboListInline}`}
          >
            {suggerimenti.map((p, i) => (
              <li
                key={p.internalId}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === attivo}
                className={`${styles.comboOption} ${i === attivo ? styles.comboOptionActive : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  scegli(p);
                }}
                data-testid={`picker-prodotto-${p.internalId}`}
              >
                <span>{p.name}</span>
                <span className={styles.comboMeta}>{p.internalId}</span>
              </li>
            ))}
          </ul>
        )}
        {aperto && !isLoading && filtro.trim() !== '' && suggerimenti.length === 0 && (
          <p className={styles.pickerHint}>Nessun prodotto trovato.</p>
        )}
      </div>

      {prodotto && (
        <ReleaseDelProdotto
          key={scelta}
          prodotto={prodotto}
          selezionate={selezionate}
          onToggle={toggle}
          onSelezionaTutte={selezionaTutte}
          onDeselezionaTutte={deselezionaTutte}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog di creazione
// ---------------------------------------------------------------------------

export function TicketCreateDialog({
  onChiudi,
  onCreato,
}: {
  onChiudi: () => void;
  onCreato?: (ticket: Ticket) => void;
}) {
  const [titolo, setTitolo] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [releases, setReleases] = useState<ReleaseSelezionata[]>([]);
  const [sorgente, setSorgente] = useState<SorgenteForm>(sorgenteIniziale);
  const { mutate, isPending, isError, error } = useCreateTicket();

  const valido = titolo.trim().length > 0 && releases.length > 0 && sorgenteValida(sorgente);

  function invia(e: React.FormEvent) {
    e.preventDefault();
    if (!valido) return;
    mutate(
      {
        title: titolo.trim(),
        ...(descrizione.trim() ? { description: descrizione.trim() } : {}),
        source: sorgenteInInput(sorgente),
        releases: releases.map((r) => ({
          productInternalId: r.productInternalId,
          releaseVersion: r.releaseVersion,
        })),
      },
      {
        onSuccess: (ticket) => {
          onCreato?.(ticket);
          onChiudi();
        },
      },
    );
  }

  return (
    <ModalPortal onClose={onChiudi}>
      <div
        className={styles.dialogOverlay}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titolo-nuovo-ticket"
        data-testid="dialog-nuovo-ticket"
      >
        <form className={`${styles.dialogCard} ${styles.dialogCardWide}`} onSubmit={invia}>
          <p className={styles.dialogTitle} id="titolo-nuovo-ticket">
            Nuovo ticket
          </p>
          <p className={styles.pickerHint} style={{ margin: '0 0 0.8rem' }}>
            Per segnalazioni esterne (advisory, CERT, cliente) anche senza CVE nota: le CVE si
            assegnano dopo, dal dettaglio del ticket.
          </p>

          <div className={styles.formGroup}>
            <label className={styles.formGroupLabel} htmlFor="ticket-titolo">
              Titolo *
            </label>
            <input
              id="ticket-titolo"
              type="text"
              className={styles.formSelect}
              value={titolo}
              maxLength={200}
              onChange={(e) => setTitolo(e.target.value)}
              placeholder="Es. Advisory vendor X su libreria TLS"
              data-testid="input-titolo-ticket"
              autoFocus
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formGroupLabel} htmlFor="ticket-descrizione">
              Descrizione
            </label>
            <textarea
              id="ticket-descrizione"
              className={styles.formTextarea}
              value={descrizione}
              maxLength={5000}
              onChange={(e) => setDescrizione(e.target.value)}
              placeholder="Contesto, fonte della segnalazione, impatto atteso…"
              data-testid="textarea-descrizione-ticket"
            />
          </div>

          <div className={styles.formGroup}>
            <SorgenteFields value={sorgente} onChange={setSorgente} />
          </div>

          <div className={styles.formGroup}>
            <span className={styles.formGroupLabel}>
              Release coinvolte * <span className={styles.comboMeta}>({releases.length})</span>
            </span>
            <ReleasePicker value={releases} onChange={setReleases} />
          </div>

          <div className={styles.formActions}>
            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={!valido || isPending}
              data-testid="btn-crea-ticket"
            >
              {isPending ? 'Creazione…' : 'Crea ticket'}
            </button>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={onChiudi}
              disabled={isPending}
              data-testid="btn-annulla-ticket"
            >
              Annulla
            </button>
            {isError && (
              <span className={styles.inlineMutError} data-testid="errore-nuovo-ticket">
                {messaggioErrore(error)}
              </span>
            )}
          </div>
        </form>
      </div>
    </ModalPortal>
  );
}
